<template>
  <div class="content-width py-4">
    <b-table
      v-if="!isMobile"
      responsive
      striped
      hover
      bordered
      head-variant="light"
      :items="items"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filter"
      :filter-included-fields="filterOn"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @filtered="onFiltered"
      empty-text="目前尚無資料"
      empty-filtered-text="沒有找到符合的訂單"
      show-empty
      @row-clicked="item => rowClick(item)"
    >
      >
      <template v-slot:head()="scope">
        <div class="text-nowrap">
          {{ scope.label }}
        </div>
      </template>
      <template v-slot:emptyfiltered="{ emptyFilteredText }">
        <div class="text-center">{{ emptyFilteredText }}</div>
      </template>
      <template v-slot:cell(order_serial)="data">
        <div style="word-break: break-all;">
          {{ data.item.order_serial }}
        </div>
      </template>
      <template v-slot:cell(created_at)="data">
        {{ format(data.item.created_at, "yyyy-MM-dd hh:mm") }}
      </template>
      <template v-slot:cell(updated_at)="data">
        {{ format(data.item.updated_at, "yyyy-MM-dd hh:mm") }}
      </template>
      <template v-slot:cell(original_price)="data">
        <div class="text-right">
          {{
            data.item.original_price
              ? toThousands(data.item.original_price)
              : ""
          }}
        </div>
      </template>
      <template v-slot:cell(price)="data">
        <div class="text-right">
          {{ data.item.price ? toThousands(data.item.price) : "" }}
        </div>
      </template>
      <template v-slot:cell(pay_status)="data">
        <span
          :class="
            !data.item.pay_status
              ? 'text-danger'
              : data.item.pay_status == 1
              ? 'text-success'
              : 'text-warning'
          "
        >
          {{ getPayStatusName(data.item.pay_status) }}
        </span>
      </template>
      <template v-slot:cell(shopping_status)="data">
        <span
          :class="
            !data.item.shopping_status
              ? 'text-danger'
              : data.item.shopping_status == 1
              ? 'text-success'
              : 'text-warning'
          "
        >
          {{ getShoppingStatusName(data.item.shopping_status) }}
        </span>
      </template>
      <template v-slot:cell(vendor_id)="data">
        {{ data.item.vendor_name }}
      </template>
      <template v-slot:cell(active)="data">
        <b-dropdown
          right
          no-caret
          variant="link"
          boundary="viewport"
          class="btn-only-icon"
        >
          <template v-slot:button-content>
            <b-icon icon="three-dots-vertical" variant="dark"></b-icon>
          </template>
          <b-dropdown-item
            href="#"
            v-if="!data.item.vendor_name && isAdmin"
            @click="edit(data.item.order_serial)"
            >需指派業務員</b-dropdown-item
          >
          <b-dropdown-item
            v-else
            href="#"
            @click="edit(data.item.order_serial)"
          >
            <b-icon
              :icon="isAdmin ? 'eye' : 'pencil-square'"
              class="mr-2"
            ></b-icon>
            {{ isAdmin ? "檢視" : "編輯" }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>

    <b-table
      v-else
      striped
      bordered
      head-variant="light"
      :items="items"
      :fields="fields_mobile"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filter"
      :filter-included-fields="filterOn"
      @filtered="onFiltered"
      empty-text="目前尚無資料"
      empty-filtered-text="沒有找到符合的訂單"
      show-empty
      @row-clicked="item => rowClick(item)"
    >
      >
      <template v-slot:head()="scope">
        <div class="text-nowrap">
          {{ scope.label }}
        </div>
      </template>
      <template v-slot:emptyfiltered="{ emptyFilteredText }">
        <div class="text-center">{{ emptyFilteredText }}</div>
      </template>

      <template v-slot:cell(show_details)="row">
        <div class="btn-only-icon">
          <b-button
            @click="row.toggleDetails"
            class="btn-link border-0"
            variant="outline-light"
            size="sm"
          >
            <b-icon
              :icon="row.detailsShowing ? 'chevron-down' : 'chevron-right'"
              variant="dark"
            ></b-icon>
          </b-button>
        </div>
      </template>
      <template v-slot:cell(order_serial)="data">
        <div style="font-size: 14px; word-break: break-all;">
          {{ data.item.order_serial }}
        </div>
      </template>
      <template v-slot:cell(active)="data">
        <b-dropdown
          right
          no-caret
          variant="link"
          boundary="viewport"
          class="btn-only-icon"
        >
          <template v-slot:button-content>
            <b-icon icon="three-dots-vertical" variant="dark"></b-icon>
          </template>
          <b-dropdown-item
            href="#"
            v-if="!data.item.vendor_name && isAdmin"
            @click="edit(data.item.order_serial)"
            >需指派業務員</b-dropdown-item
          >
          <b-dropdown-item
            v-else
            href="#"
            @click="edit(data.item.order_serial)"
          >
            {{ isAdmin ? "檢視" : "編輯" }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:row-details="row">
        <b-card>
          <b-row class="mb-3">
            <b-col sm="3"><b>訂購公司</b></b-col>
            <b-col>{{ row.item.company_name }}</b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="3"><b>訂購時間</b></b-col>
            <b-col>
              {{ format(row.item.created_at, "yyyy-MM-dd hh:mm") }}
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="3"><b>原始金額</b></b-col>
            <b-col>{{
              row.item.original_price
                ? toThousands(row.item.original_price)
                : ""
            }}</b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="3"><b>成交金額</b></b-col>
            <b-col>{{
              row.item.price ? toThousands(row.item.price) : ""
            }}</b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="3"><b>付款狀態</b></b-col>
            <b-col>
              <span
                :class="
                  !row.item.pay_status
                    ? 'text-danger'
                    : row.item.pay_status == 1
                    ? 'text-success'
                    : 'text-warning'
                "
              >
                {{ getPayStatusName(row.item.pay_status) }}
              </span>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="3"><b>出貨狀態</b></b-col>
            <b-col>
              <span
                :class="
                  !row.item.shopping_status
                    ? 'text-danger'
                    : row.item.shopping_status == 1
                    ? 'text-success'
                    : 'text-warning'
                "
              >
                {{ getShoppingStatusName(row.item.shopping_status) }}
              </span>
            </b-col>
          </b-row>
          <b-row class="mb-3" v-if="isAdmin">
            <b-col sm="3"><b>業務員</b></b-col>
            <b-col>{{ row.item.vendor_name || "(需指派業務員)" }}</b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="3"><b>更新時間</b></b-col>
            <b-col>
              {{ format(row.item.updated_at, "yyyy-MM-dd hh:mm") }}
            </b-col>
          </b-row>
          <b-row class="">
            <b-col class="text-center">
              <b-button
                size="sm"
                squared
                variant="cms_main"
                @click="row.toggleDetails"
                >收起詳細資訊</b-button
              >
            </b-col>
          </b-row>
        </b-card>
      </template>
    </b-table>

    <div class="row align-items-center" v-if="totalRows">
      <div class="col-sm col-12 text-right text-sm-left">
        顯示第 {{ currentPage == 1 ? 1 : perPage * (currentPage - 1) + 1 }} 至
        {{
          currentPage == 1
            ? totalRows > perPage
              ? perPage
              : totalRows
            : perPage * (currentPage - 1) + perPage > totalRows
            ? totalRows
            : perPage * (currentPage - 1) + perPage
        }}
        項結果，共 {{ totalRows }} 項
      </div>
      <div class="col-sm col-12">
        <b-pagination
          v-if="totalRows > perPage"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          first-number
          last-number
          class="mb-sm-0 mb-3 mt-sm-0 mt-3 cms_main"
        ></b-pagination>
      </div>
    </div>
    <b-modal
      id="modalVendorModal"
      size="lg"
      :title="modal_title"
      footer-class="d-flex justify-content-between"
      no-close-on-backdrop
      centered
      tall
      :hide-footer="isAdmin"
      scrollable
      @ok="onSubmit"
      @show="showModal"
      @hidden="hiddenModal"
    >
      <div class="row" v-if="edit_data">
        <div class="col-12 col-lg-6">
          <div class="row mb-2">
            <div class="col-auto text-secondary" style="width: 100px">
              訂購公司
            </div>
            <div class="col">{{ edit_data.company_name }}</div>
          </div>
          <div class="row mb-2">
            <div class="col-auto text-secondary" style="width: 100px">
              訂購人
            </div>
            <div class="col">{{ edit_data.name }}</div>
          </div>
          <div class="row mb-2">
            <div class="col-auto text-secondary" style="width: 100px">
              訂購時間
            </div>
            <div class="col">
              {{ format(edit_data.created_at, "yyyy-MM-dd hh:mm") }}
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="row mb-2">
            <div class="col-auto text-secondary" style="width: 100px">統編</div>
            <div class="col">{{ edit_data.tax_id }}</div>
          </div>
          <div class="row mb-2">
            <div class="col-auto text-secondary" style="width: 100px">電話</div>
            <div class="col">{{ edit_data.telephone }}</div>
          </div>
          <div class="row mb-2">
            <div class="col-auto text-secondary" style="width: 100px">地址</div>
            <div class="col">
              {{
                edit_data.city_name + edit_data.town_name + edit_data.address
              }}
            </div>
          </div>
        </div>
        <div class="col-12">
          <hr />
          <div class="row mb-2 align-items-center" v-if="isAdmin">
            <div class="col-auto" style="width: 100px">
              負責業務:
            </div>
            <div class="col" v-if="edit_data.vendor_id">
              {{ gatSalesName(edit_data.vendor_id) }}
            </div>
            <div class="col" v-else>
              <div class="d-flex my-2">
                <b-form-select class="rounded-0 col" v-model="chooseSales">
                  <b-form-select-option value="" disabled selected
                    >請選擇</b-form-select-option
                  >
                  <b-form-select-option
                    v-for="item in salesList"
                    :key="item.id"
                    :value="item.id"
                    >{{ item.name }}</b-form-select-option
                  >
                </b-form-select>
                <div class="col-auto">
                  <b-button
                    squared
                    variant="cms_main"
                    type="button"
                    @click="assignStatus"
                    >指派</b-button
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 mt-2">
              <div class="row mb-2 align-items-center no-gutters">
                <div class="col-auto" style="width: 100px;">付款狀態:</div>
                <div class="col mr-4 ml-2">
                  <select
                    class="custom-select rounded-0"
                    :disabled="isAdmin"
                    v-model="form.pay_status"
                    :class="{
                      'text-danger': form.pay_status == 0,
                      'text-success': form.pay_status == 1,
                      'text-cms_sub': form.pay_status == 2
                    }"
                  >
                    <option :value="0">未付款</option>
                    <option :value="2">部分付款</option>
                    <option :value="1">已付款</option>
                  </select>
                </div>
              </div>
              <div class="row mb-2 align-items-center no-gutters">
                <div class="col-auto" style="width: 100px;">出貨狀態:</div>
                <div class="col mr-4 ml-2">
                  <select
                    class="custom-select rounded-0"
                    v-model="form.shopping_status"
                    :disabled="isAdmin"
                    :class="{
                      'text-danger': form.shopping_status == 0,
                      'text-success': form.shopping_status == 1,
                      'text-cms_sub': form.shopping_status == 2
                    }"
                  >
                    <option :value="0">未出貨</option>
                    <option :value="2">部分出貨</option>
                    <option :value="1">已出貨</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="col-sm-6 mt-2">
              <div class="row mb-2 align-items-center no-gutters">
                <div class="col-auto" style="width: 100px; line-height:38px">
                  總金額:
                </div>
                <div
                  class="col mr-4 ml-2 text-right"
                  style="text-decoration:line-through;"
                >
                  {{ "$" + toThousands(totalPrice) }}
                </div>
              </div>
              <div class="row mb-2 align-items-center no-gutters">
                <div class="col-auto" style="width: 100px; line-height:38px">
                  成交總金額:
                </div>
                <div class="col mr-4 ml-2 text-right">
                  <div>{{ "$" + toThousands(edit_data.price) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 mt-2">
          <div class="mb-2">詳細商品清單：</div>
          <table class="table b-table" :class="{ 'b-table-stacked': isMobile }">
            <tbody>
              <tr
                class="border border-top-0"
                v-for="(item, index) in edit_data.info"
                :key="item.product_serial_no"
              >
                <td
                  class="align-middle"
                  :style="{ width: isMobile ? 'auto' : '100px' }"
                  :class="{ 'border-top': isMobile }"
                >
                  <img
                    :src="imageUrl(item.cover_image)"
                    alt=""
                    style="width: 100%"
                  />
                </td>
                <td class="align-middle">
                  <div>
                    <span v-if="!isMobile" class="text-cms_main">{{
                      item.product_serial_no
                    }}</span>
                    <div v-else class="row mb-3 pb-3 border-bottom">
                      <div class="col-auto" style="width: 100px">零件編號</div>
                      <div class="col text-cms_main">
                        {{ item.product_serial_no }}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span v-if="!isMobile">{{ item.product_name }}</span>
                    <div v-else class="row">
                      <div class="col-auto" style="width: 100px">零件名稱</div>
                      <div class="col">
                        {{ item.product_name }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="align-middle" :class="{ 'text-right': !isMobile }">
                  <span v-if="!isMobile">{{
                    "$" + toThousands(item.price)
                  }}</span>
                  <div v-else class="row">
                    <div class="col-auto" style="width: 100px">單價</div>
                    <div class="col">
                      {{ "$" + toThousands(item.price) }}
                    </div>
                  </div>
                </td>
                <td class="align-middle" :class="{ 'text-right': !isMobile }">
                  <span v-if="!isMobile" class="text-nowrap">{{
                    "X " + item.count
                  }}</span>
                  <div v-else class="row">
                    <div class="col-auto" style="width: 100px">數量</div>
                    <div class="col">
                      {{ item.count }}
                    </div>
                  </div>
                </td>
                <td class="align-middle " :class="{ 'text-right': !isMobile }">
                  <span
                    v-if="!isMobile"
                    style="text-decoration: line-through;"
                    >{{ "$" + toThousands(item.price * item.count) }}</span
                  >
                  <div v-else class="row">
                    <div class="col-auto" style="width: 100px">小計</div>
                    <div class="col" style="text-decoration: line-through;">
                      {{ "$" + toThousands(item.price * item.count) }}
                    </div>
                  </div>
                </td>
                <td class="align-middle " :class="{ 'text-right': !isMobile }">
                  <div v-if="!isMobile">
                    <span v-if="isAdmin" class="text-cms_sub">{{
                      "$" + toThousands(item.total_price)
                    }}</span>
                    <b-form-input
                      v-else
                      class="rounded-0 text-right"
                      v-model.number="form.items[index].total_price"
                      type="number"
                      min="0"
                      :disabled="isAdmin"
                    ></b-form-input>
                  </div>
                  <div v-else class="row">
                    <div class="col-auto" style="width: 100px">成交金額</div>
                    <div class="col text-cms_sub">
                      <span v-if="isAdmin">
                        {{ "$" + toThousands(item.total_price) }}
                      </span>
                      <b-form-input
                        v-else
                        class="rounded-0 text-right"
                        v-model.number="form.items[index].total_price"
                        type="number"
                        min="0"
                        :disabled="isAdmin"
                      ></b-form-input>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button squared variant="outline-dark" type="button" @click="cancel()"
          >取消</b-button
        >
        <b-button squared variant="cms_main" type="submit" @click="ok()"
          >儲存</b-button
        >
      </template>
    </b-modal>
  </div>
</template>
<style lang="scss" scoped>
.table-responsive {
  font-size: 14px;
}
.form-control,
.custom-select {
  &:disabled {
    border: none;
    background-color: transparent;
    background: none;
  }
}
</style>
<script>
import "@/assets/js/dateFormat.js";
import { Toast } from "@/plugins/sweetalert2.js";
import { Order, Account } from "@/assets/api/cms.js";

export default {
  name: "Order",
  props: ["search", "isMobile", "isAdmin", "token"],
  data() {
    return {
      imageOnerror: "/img/image_onerror.jpg",
      currentPage: 1,
      edit_id: null,
      edit_data: null,
      sortBy: "updated_at",
      sortDesc: true,
      fields: [
        {
          key: "order_serial",
          label: "訂單編號",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "company_name",
          label: "訂購公司",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "name",
          label: "訂購人",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "created_at",
          label: "訂購時間",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "original_price",
          label: "原始金額",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "price",
          label: "成交金額",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "pay_status",
          label: "付款狀態",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "shopping_status",
          label: "出貨狀態",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "vendor_id",
          label: "業務員",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "updated_at",
          label: "更新時間",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "active",
          label: "",
          tdClass: "align-middle",
          thStyle: "width:35px"
        }
      ],
      fields_mobile: [
        {
          key: "show_details",
          label: "",
          tdClass: "align-middle",
          thStyle: "width:35px"
        },
        {
          key: "order_serial",
          label: "訂單編號",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "name",
          label: "訂購人",
          tdClass: "align-middle",
          sortable: true
        },
        {
          key: "active",
          label: "",
          tdClass: "align-middle",
          thStyle: "width:35px"
        }
      ],
      filter: null,
      filterOn: ["order_serial"],
      form: {
        pay_status: "",
        shopping_status: "",
        items: []
      },
      isModalShow: false,
      items: null,
      perPage: 10,
      state: {
        price: null
      },
      totalRows: null,
      salesList: null,
      modal_title: null,
      chooseSales: ""
    };
  },
  created() {
    this.perPage = process.env.VUE_APP_TABLE_ROW_LENGTH || 15;
    this.filter = this.search;
    this.getOrder();
  },
  computed: {
    totalPrice() {
      if (!this.edit_data) return;
      let sum = 0;
      this.edit_data.info.forEach(val => {
        sum += Number(val.price) * Number(val.count);
      });
      return sum;
    }
  },
  watch: {
    "form.items": {
      handler: function(newVal) {
        if (!newVal.length) return;
        let total = 0;
        newVal.forEach(element => {
          total += element.total_price;
        });
        this.edit_data.price = total;
      },
      deep: true
    },
    isAdmin(newVal) {
      if (newVal) {
        Account.get("", this.token)
          .then(res => {
            if (res.status <= 201) {
              this.salesList = res.data.Sales.filter(Sales => Sales.status);
            } else {
              Toast.fire({
                icon: "error",
                title: "權限狀態讀取失敗",
                text: res.data.error
              });
            }
          })
          .catch(error => {
            console.error(error);
          });
      }
      this.fields.forEach((field, index) => {
        if (field.key == "vendor_id") {
          if (!newVal) {
            this.fields.splice(index, 1);
          }
        } else {
          if (newVal) {
            this.fields.push({
              key: "vendor_id",
              label: "業務員",
              tdClass: "align-middle",
              sortable: true
            });
          }
        }
      });
    }
  },
  methods: {
    rowClick(item) {
      this.edit(item.order_serial);
    },
    getOrder() {
      Order.get("", this.token)
        .then(res => {
          if (res.status <= 201) {
            this.items = res.data;
            this.totalRows = this.items.length;
          } else {
            Toast.fire({
              icon: "error",
              title: "訂單資料讀取失敗",
              text: res.data.error
            });
          }
        })
        .catch(error => {
          console.error(error);
        });
    },
    assignStatus() {
      Order.assign(this.edit_id, { assignID: this.chooseSales }, this.token)
        .then(res => {
          if (res.data.status == "success") {
            Toast.fire({
              icon: "success",
              title: "指派成功"
            });
            Order.get("", this.token)
              .then(res => {
                this.items = res.data;
                this.totalRows = this.items.length;
              })
              .catch(error => {
                console.log(error);
              });
            this.$root.$emit("bv::hide::modal", "modalVendorModal", "#btnShow");
          } else {
            Toast.fire({
              icon: "error",
              title: "指派失敗",
              text: res.data.error
            });
          }
        })
        .catch(error => {
          console.error(error);
        });
    },
    gatSalesName(id) {
      if (this.isAdmin && this.salesList) {
        for (let index = 0; index < this.salesList.length; index++) {
          if (this.salesList[index].id == id) {
            return this.salesList[index].name;
          }
        }
        return "(此業務員已不存在)";
      }
    },
    toThousands(num) {
      return (num || 0).toString().replace(/(\d)(?=(?:\d{3})+$)/g, "$1,");
    },
    imageUrl(url) {
      return url
        ? (process.env.VUE_APP_API_BASE_URL || "") + url
        : this.imageOnerror;
    },
    format(date, formatText) {
      // "yyyy-MM-dd"
      return new Date(date).format(formatText);
    },
    getPayStatusName(id) {
      switch (id) {
        case 0:
          return "未付款";
        case 1:
          return "已付款";
        case 2:
          return "部分付款";
      }
    },
    getShoppingStatusName(id) {
      switch (id) {
        case 0:
          return "未出貨";
        case 1:
          return "已出貨";
        case 2:
          return "部分出貨";
      }
    },
    imageError() {
      this.$nextTick(() => {
        var imgList = document.querySelectorAll("img");
        Array.prototype.forEach.call(imgList, item => {
          item.onerror = () => {
            console.log(item);
            item.src = this.imageOnerror;
          };
        });
      });
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    resetForm() {
      this.edit_id = null;
      this.state.name = null;
      this.form.pay_status = "";
      this.form.shopping_status = "";
      // this.form.items = [];
    },
    // checkFormValidity() {
    //   const valid = this.$refs.form.checkValidity();
    //   this.checkFormState();
    //   return valid;
    // },
    // checkFormState() {
    //   this.form.name ? (this.state.name = null) : (this.state.name = false);
    // },
    edit(id) {
      this.resetForm();
      this.isEdit = true;
      this.edit_id = id;
      this.$root.$emit("bv::show::modal", "modalVendorModal", "#btnShow");
    },
    showModal() {
      this.isModalShow = true;
      Order.get(this.edit_id, this.token)
        .then(res => {
          if (res.status <= 201) {
            this.edit_data = res.data;
            this.modal_title = "訂單編號: " + res.data.order_serial;
            this.form.pay_status = res.data.pay_status;
            this.form.shopping_status = res.data.shopping_status;
            this.edit_data.info.forEach((element, index) => {
              this.$set(this.form.items, index, {
                id: element.id,
                total_price: element.total_price
              });
            });
          } else {
            Toast.fire({
              icon: "error",
              title: "訂單資料讀取失敗",
              text: res.data.error
            });
          }
        })
        .catch(error => {
          console.error(error);
        });
      this.imageError();
    },
    hiddenModal() {
      this.isModalShow = false;
      this.resetForm();
    },
    onSubmit(evt) {
      evt.preventDefault();
      this.handleSubmit();
    },
    handleSubmit() {
      // if (!this.checkFormValidity()) {
      //   return;
      // }
      // for (const value of Object.keys(this.state)) {
      //   if (this.state[value] === false) {
      //     return;
      //   }
      // }
      let newData = Object.assign({}, this.form);

      Order.update(this.edit_id, newData, this.token)
        .then(res => {
          if (res.status <= 201) {
            Toast.fire({
              icon: "success",
              title: "編輯成功"
            });
            this.getOrder();
            this.$root.$emit("bv::hide::modal", "modalVendorModal", "#btnShow");
          } else {
            Toast.fire({
              icon: "error",
              title: "編輯失敗",
              text: res.data.error
            });
          }
        })
        .catch(err => {
          console.error(err);
        });
    }
  }
};
</script>
